import {
  Component,
  Host,
  h,
  Prop,
  Element,
  State,
  Event,
  EventEmitter,
  Watch
} from "@stencil/core";

import { MercuryBundles, getIconPath } from "@genexus/mercury";

import { Locale } from "../../../common/locale";
import { LabelPosition } from "../../../common/types";
import { EntityData } from "../../../common/types";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/edit",
  "components/button",
  "components/icon",
  "utils/form"
];
const SELECT_DEFAULT_ICON = getIconPath({
  category: "gemini-tools",
  name: "show-more-horizontal",
  colorType: "primary"
});
const CLEAR_ICON = getIconPath({
  category: "gemini-tools",
  name: "reset",
  colorType: "primary"
});

@Component({
  tag: "gx-ide-entity-selector",
  styleUrl: "entity-selector.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/entity-selector"]
})
export class GxIdeEntitySelector {
  #componentLocale: any;

  @Element() el: HTMLGxIdeEntitySelectorElement;

  /**
   * Adds/removes a CSS class on the host. It removes focus-within styles when
   * focus is on a button.
   */
  @State() buttonHasFocus = false;

  @State() iconSrc: string = null;

  /**
   * Default value for the component. Used when the 'X' button is pressed.
   */
  @Prop() readonly defaultValue?: EntityData | null | undefined;

  /**
   * This attribute lets you specify if the element is disabled.
   */
  @Prop({ reflect: true }) readonly disabled: boolean = false;

  /**
   * The label caption. Only visible if "labelPosition" is not "none".
   * I no "labelCaption" is provided, a generic caption "Select Entity" will be provided
   */
  @Prop() readonly labelCaption?: string;

  /**
   * The label position
   */
  @Prop({ reflect: true }) readonly labelPosition?: LabelPosition =
    "block-start";

  /**
   * This property specifies the `name` of the control when used in a form.
   */
  @Prop({ reflect: true }) readonly name?: string;

  /**
   * Callback invoked when the action button is pressed. Returns the new value.
   */
  @Prop() readonly selectEntityCallback!: () => Promise<EntityData> | null;

  /**
   * Value currently assigned.
   */
  @Prop({ mutable: true }) value: EntityData | null | undefined;
  @Watch("value")
  valueChangedHandler() {
    this.valueChanged.emit(this.value);
    this.#updateIconSrc();
  }

  /**
   * Emits the input value every time it changes
   */
  @Event() valueChanged: EventEmitter<EntityData>;

  // 6.Component Lifecycle Events

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
  }

  connectedCallback() {
    this.#updateIconSrc();
  }

  #btnClearClickHandler = () => {
    this.value = this.defaultValue;
    this.iconSrc = this.defaultValue?.iconSrc;
  };

  #btnSelectClickHandler = () => {
    this.selectEntityCallback().then(result => {
      if (result) {
        this.value = result;
      }
    });
  };

  #buttonFocusHandler = (e: FocusEvent) => {
    if (e.type === "focus") {
      this.buttonHasFocus = true;
    } else if (e.type === "blur") {
      this.buttonHasFocus = false;
    }
  };

  #renderControl = (): HTMLDivElement => {
    return (
      <div part="wrapper" class="wrapper">
        <ch-edit
          accessibleName={
            this.labelPosition === "none" &&
            this.#componentLocale.entitySelectorInputAccessibleName
          }
          class="input"
          disabled={this.disabled}
          name={this.name || this.#componentLocale.entitySelectorNameAttribute}
          startImgSrc={this.iconSrc}
          readonly
          value={this.value?.name || this.defaultValue?.name}
          id="entity-input"
        ></ch-edit>

        <button
          part="button-clear"
          class="icon-button"
          aria-label={this.#componentLocale.clearButtonLabel}
          title={this.#componentLocale.clearButtonLabel}
          type="button"
          disabled={this.disabled}
          onClick={this.#btnClearClickHandler}
          onFocus={this.buttonHasFocus ? this.#buttonFocusHandler : undefined}
          onBlur={!this.buttonHasFocus ? this.#buttonFocusHandler : undefined}
        >
          <ch-image
            class="icon-m"
            src={CLEAR_ICON}
            disabled={this.disabled}
          ></ch-image>
        </button>

        <button
          class="icon-button"
          aria-label={this.#componentLocale.selectButtonLabel}
          title={this.#componentLocale.selectButtonLabel}
          type="button"
          disabled={!this.selectEntityCallback || this.disabled}
          onClick={this.#btnSelectClickHandler}
          onBlur={!this.buttonHasFocus ? this.#buttonFocusHandler : undefined}
          onFocus={this.buttonHasFocus ? this.#buttonFocusHandler : undefined}
        >
          <ch-image
            class="icon-m"
            src={SELECT_DEFAULT_ICON}
            disabled={this.disabled}
          ></ch-image>
        </button>
      </div>
    );
  };

  #updateIconSrc = () => {
    if (this.value && this.value.iconSrc) {
      this.iconSrc = this.value.iconSrc;
    }
  };

  render() {
    return (
      <Host
        class={{
          "entity-selector--button-has-focus": this.buttonHasFocus
        }}
      >
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        {this.labelPosition !== "none" ? (
          <div
            class={{
              "field": true,
              "field-block": this.labelPosition === "block-start",
              "field-inline": this.labelPosition === "inline-start"
            }}
          >
            <label class="label" htmlFor="entity-input">
              {this.labelCaption ||
                this.#componentLocale.entitySelectorInputAccessibleName}
            </label>
            {this.#renderControl()}
          </div>
        ) : (
          this.#renderControl()
        )}
      </Host>
    );
  }
}

export type EntitySelectorLabels = {
  buttonClearLabel: string;
  buttonSelectLabel: string;
};
